import React, { useEffect, useRef, useState } from "react";
import "./HowToPlayPage.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { GAMES } from "../constants/games";
import {
  HangmanIcon,
  FillerIcon,
  FlashcardsIcon,
  DinoIcon,
  MazeIcon,
  OpsTicklesIcon,
  ChessIcon,
  SolitaireIcon,
  StarIcon,
} from "./HomeIcons";

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const ALL_ID = "all";

const MODE_ICON_GRADIENTS = {
  purple: "linear-gradient(135deg, #f4d35e, #e4c14a)",
  pink: "linear-gradient(135deg, #7c3aed, #4c1d95)",
  yellow: "linear-gradient(135deg, #1e293b, #0f172a)",
  blue: "linear-gradient(135deg, #3b82f6, #1e40af)",
  green: "linear-gradient(135deg, #22c55e, #166534)",
  red: "linear-gradient(135deg, #ff6b6b, #ff0000)",
};

const HOW_TO_GAMES = [
  {
    id: "hangman",
    icon: HangmanIcon,
    gradient: "linear-gradient(135deg, #6d28d9, #4c1d95)",
    stepsKey: "howToHangmanSteps",
    tipKey: "howToHangmanTip",
    video: `${process.env.PUBLIC_URL}/howto/hangman.mp4`,
    videoWebm: `${process.env.PUBLIC_URL}/howto/hangman.webm`,
  },
  {
    id: "filler",
    icon: FillerIcon,
    gradient: "linear-gradient(135deg, #2563eb, #1e3a8a)",
    stepsKey: "howToFillerSteps",
    tipKey: "howToFillerTip",
    video: `${process.env.PUBLIC_URL}/howto/filler.mp4`,
    videoWebm: `${process.env.PUBLIC_URL}/howto/filler.webm`,
  },
  {
    id: "flashcards",
    icon: FlashcardsIcon,
    gradient: MODE_ICON_GRADIENTS.purple,
    stepsKey: "howToFlashcardsSteps",
    tipKey: "howToFlashcardsTip",
    video: `${process.env.PUBLIC_URL}/howto/flashcards.mp4`,
    videoWebm: `${process.env.PUBLIC_URL}/howto/flashcards.webm`,
  },
  {
    id: "dino",
    icon: DinoIcon,
    gradient: "linear-gradient(135deg, #db2777, #9d174d)",
    stepsKey: "howToDinoSteps",
    tipKey: "howToDinoTip",
    video: `${process.env.PUBLIC_URL}/howto/dino.mp4`,
    videoWebm: `${process.env.PUBLIC_URL}/howto/dino.webm`,
  },
  {
    id: "maze",
    icon: MazeIcon,
    gradient: "linear-gradient(135deg, #be185d, #831843)",
    stepsKey: "howToMazeSteps",
    tipKey: "howToMazeTip",
    video: `${process.env.PUBLIC_URL}/howto/maze.mp4`,
    videoWebm: `${process.env.PUBLIC_URL}/howto/maze.webm`,
  },
  {
    id: "opsTickles",
    icon: OpsTicklesIcon,
    gradient: "linear-gradient(135deg, #0f172a, #1e293b)",
    stepsKey: "howToOpsTicklesSteps",
    tipKey: "howToOpsTicklesTip",
    video: `${process.env.PUBLIC_URL}/howto/opsTickles.mp4`,
    videoWebm: `${process.env.PUBLIC_URL}/howto/opsTickles.webm`,
  },
  {
    id: "chess",
    icon: ChessIcon,
    gradient: "linear-gradient(135deg, #14532d, #166534)",
    stepsKey: "howToChessSteps",
    tipKey: "howToChessTip",
    video: `${process.env.PUBLIC_URL}/howto/chess.mp4`,
    videoWebm: `${process.env.PUBLIC_URL}/howto/chess.webm`,
  },
  {
    id: "solitaire",
    icon: SolitaireIcon,
    gradient: "linear-gradient(135deg, #c6d94a, #8faa22)",
    stepsKey: "howToSolitaireSteps",
    tipKey: "howToSolitaireTip",
  },
];

const getIconGradient = (game, mode) => {
  if (mode === "red") {
    return MODE_ICON_GRADIENTS.red;
  }
  if (game.id === "flashcards") {
    return MODE_ICON_GRADIENTS[mode] || game.gradient;
  }
  return game.gradient;
};

const HowToPlayPage = ({ onSelectGame, initialGameId }) => {
  const { t } = useLanguage();
  const { mode } = useTheme();
  const [selectedId, setSelectedId] = useState(() => {
    if (initialGameId && HOW_TO_GAMES.some((g) => g.id === initialGameId)) {
      return initialGameId;
    }
    return ALL_ID;
  });
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const videoRef = useRef(null);

  useEffect(() => {
    if (
      initialGameId &&
      HOW_TO_GAMES.some((game) => game.id === initialGameId)
    ) {
      setSelectedId(initialGameId);
    }
  }, [initialGameId]);

  const isAll = selectedId === ALL_ID;
  const selected = HOW_TO_GAMES.find((game) => game.id === selectedId);
  const game = !isAll ? GAMES[selectedId] : null;
  const SelectedIcon = selected?.icon;
  const selectedGradient = selected
    ? getIconGradient(selected, mode)
    : MODE_ICON_GRADIENTS[mode] || MODE_ICON_GRADIENTS.pink;
  const steps = selected
    ? t(selected.stepsKey).split("\n").filter(Boolean)
    : [];

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed, selectedId]);

  return (
    <section className="htp-page">
      <header className="htp-header">
        <p className="htp-eyebrow">{t("howToEyebrow")}</p>
        <h2 className="htp-heading">{t("howToTitle")}</h2>
        <p className="htp-subtitle">{t("howToSubtitle")}</p>
      </header>

      <div className="htp-game-picker" role="tablist" aria-label={t("howToTitle")}>
        <button
          type="button"
          role="tab"
          aria-selected={isAll}
          className={`htp-game-btn ${isAll ? "is-active" : ""}`}
          onClick={() => setSelectedId(ALL_ID)}
        >
          <span
            className="htp-game-btn-icon htp-game-btn-icon--all"
            style={{ background: MODE_ICON_GRADIENTS[mode] || MODE_ICON_GRADIENTS.pink }}
          >
            <StarIcon size={14} solid />
          </span>
          <span className="htp-game-btn-label">{t("howToAllLabel")}</span>
        </button>
        {HOW_TO_GAMES.map((entry) => {
          const { id, icon: Icon } = entry;
          const meta = GAMES[id];
          const isActive = id === selectedId;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`htp-game-btn ${isActive ? "is-active" : ""}`}
              onClick={() => setSelectedId(id)}
            >
              <span
                className="htp-game-btn-icon"
                style={{ background: getIconGradient(entry, mode) }}
              >
                <Icon />
              </span>
              <span className="htp-game-btn-label">{t(meta.nameKey)}</span>
            </button>
          );
        })}
      </div>

      {isAll ? (
        <div className="htp-panel htp-panel--all" role="tabpanel">
          <div className="htp-all-intro">
            <h3 className="htp-game-title">{t("howToAllTitle")}</h3>
            <p className="htp-game-blurb">{t("howToAllSubtitle")}</p>
          </div>
          <div className="htp-all-grid">
            {HOW_TO_GAMES.map((entry) => {
              const { id, icon: Icon } = entry;
              const meta = GAMES[id];
              return (
                <button
                  key={id}
                  type="button"
                  className="htp-all-card"
                  onClick={() => setSelectedId(id)}
                >
                  <span
                    className="htp-all-card-icon"
                    style={{ background: getIconGradient(entry, mode) }}
                  >
                    <Icon />
                  </span>
                  <span className="htp-all-card-body">
                    <span className="htp-all-card-name">{t(meta.nameKey)}</span>
                    <span className="htp-all-card-hint">{t("howToCardHelp")}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="htp-panel" role="tabpanel">
          <div className="htp-video-wrap">
            <div className="htp-video-frame">
              {selected.video ? (
                <video
                  key={selected.id}
                  ref={videoRef}
                  className="htp-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                  onLoadedMetadata={(event) => {
                    event.currentTarget.playbackRate = playbackSpeed;
                  }}
                  aria-label={`${t(game.nameKey)} ${t("howToDemoBadge")}`}
                >
                  <source src={selected.videoWebm} type="video/webm" />
                  <source src={selected.video} type="video/mp4" />
                </video>
              ) : (
                <div
                  className="htp-video htp-video-placeholder"
                  style={{ background: selectedGradient }}
                  aria-hidden="true"
                >
                  <SelectedIcon />
                </div>
              )}
              <span className="htp-video-badge">
                {selected.video ? t("howToDemoBadge") : t("howToGuideBadge")}
              </span>
            </div>

            {selected.video && (
              <div
                className="htp-speed"
                role="group"
                aria-label={t("howToPlaybackSpeed")}
              >
                <span className="htp-speed-label">{t("howToPlaybackSpeed")}</span>
                <div className="htp-speed-options">
                  {PLAYBACK_SPEEDS.map((speed) => {
                    const isActive = playbackSpeed === speed;
                    return (
                      <button
                        key={speed}
                        type="button"
                        className={`htp-speed-btn ${isActive ? "is-active" : ""}`}
                        aria-pressed={isActive}
                        onClick={() => setPlaybackSpeed(speed)}
                      >
                        {speed === 1 ? "1×" : `${speed}×`}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="htp-info">
            <div className="htp-info-top">
              <h3 className="htp-game-title">{t(game.nameKey)}</h3>
              <p className="htp-game-blurb">{t(game.descriptionKey)}</p>
            </div>

            <ol className="htp-steps">
              {steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>

            <p className="htp-tip">{t(selected.tipKey)}</p>

            <button
              type="button"
              className="htp-play-btn"
              onClick={() => onSelectGame?.(selectedId)}
            >
              {t("howToPlayCta")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default HowToPlayPage;
